import { useEffect, useState, useCallback } from "react";
import { API } from "@/App";
import { format, subDays } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UserX, ChevronDown, ChevronUp } from "lucide-react";
import { DateInputTR } from "@/components/ui/date-picker-tr";

const ymd = (d) => format(d, "yyyy-MM-dd");
const fmt = (d) => (d ? d.split("-").reverse().join(".") : "");
const TYPE = { one_on_one: "Özel Ders", duet: "Düet", duo: "Düet", group: "Grup Dersi" };

const PersonRow = ({ p, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`noshow-row-${idx}`}>
        <td className="p-3 text-grey">{idx + 1}</td>
        <td className="p-3"><p className="font-medium">{p.member_name}</p><p className="text-xs text-grey">{p.member_no || ""}{!p.is_app_registered && " · Kayıtsız"}</p></td>
        <td className="p-3 text-sm">{p.phone || "-"}</td>
        <td className="p-3 text-center"><Badge className={p.count >= 3 ? "bg-red-100 text-red-700" : p.count === 2 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"} data-testid={`noshow-count-${idx}`}>{p.count}×</Badge></td>
        <td className="p-3 text-center text-sm">{p.burned}</td>
        <td className="p-3 text-sm">{fmt(p.dates[0]?.date)}</td>
        <td className="p-3">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50/70"><td colSpan={7} className="px-6 py-2">
          {p.dates.map((d, i) => (
            <p key={i} className="text-xs text-slate-700 py-0.5">{fmt(d.date)} {d.start_time} · {TYPE[d.training_type] || d.training_type || "-"} · {d.trainer_name || "-"}{d.card_name ? ` · ${d.card_name}` : d.payment_type === "package" ? " · Paket" : ""}</p>
          ))}
        </td></tr>
      )}
    </>
  );
};

export default function NoShowReport({ businessId }) {
  const [range, setRange] = useState([ymd(subDays(new Date(), 90)), ymd(new Date())]);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/businesses/${businessId}/reports/no-shows?start_date=${range[0]}&end_date=${range[1]}`, { credentials: "include" });
    if (res.ok) setData(await res.json());
  }, [businessId, range]);
  useEffect(() => { load(); }, [load]);
  const s = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="noshow-report">
      <div className="flex flex-wrap items-end gap-2">
        {[30, 90, 365].map((n) => (
          <Button key={n} variant="outline" size="sm" className="rounded-xl" onClick={() => setRange([ymd(subDays(new Date(), n)), ymd(new Date())])} data-testid={`noshow-preset-${n}`}>Son {n === 365 ? "1 yıl" : `${n} gün`}</Button>
        ))}
        <DateInputTR value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="rounded-xl w-40" data-testid="noshow-start" />
        <DateInputTR value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="rounded-xl w-40" data-testid="noshow-end" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[["Gelmeme", s.no_shows || 0, "noshow-stat-total"], ["Kişi", s.people || 0, "noshow-stat-people"], ["Gelmeme Oranı", `%${s.rate || 0}`, "noshow-stat-rate"]].map(([l, v, id]) => (
          <Card key={id} className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-grey">{l}</p><p className="text-2xl font-bold text-navy" data-testid={id}>{v}</p></CardContent></Card>
        ))}
      </div>
      <Card className="border-0 shadow-sm"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-grey"><tr>
            <th className="p-3 text-left">#</th><th className="p-3 text-left">Kişi</th><th className="p-3 text-left">Telefon</th>
            <th className="p-3">Gelmeme</th><th className="p-3">Yakılan</th><th className="p-3 text-left">Son</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {(data?.people || []).map((p, i) => <PersonRow key={p.member_id || i} p={p} idx={i} />)}
            {data && !data.people.length && (
              <tr><td colSpan={7} className="p-8 text-center text-grey" data-testid="noshow-empty"><UserX className="w-8 h-8 mx-auto mb-2 opacity-40" />Bu aralıkta "Gelmedi" kaydı yok.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>
      <p className="text-xs text-grey">"Gelmedi (No-show)" nedeniyle iptal edilen tüm dersler (özel, düet, grup) dahildir. Satıra tıklayarak tarihleri görün.</p>
    </div>
  );
}
